import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trophy, ChartLine, Wallet, Medal, TrendUp, TrendDown, Briefcase } from '@phosphor-icons/react';
import { formatCurrency, formatPercent } from '../lib/utils';
import api from '../lib/api';

const UserProfileModal = ({ username, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/users/profile/${encodeURIComponent(username)}`);
        if (!cancelled) setProfile(res.data);
      } catch (e) {
        if (!cancelled) setErr('Failed to load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [username]);

  const holdings = profile?.holdings || [];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="user-profile-modal"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg glass-card overflow-hidden relative max-h-[90vh] overflow-y-auto"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-300 z-10"
            data-testid="profile-modal-close"
          >
            <X size={18} />
          </button>

          <div className="bg-gradient-to-br from-yellow-500/80 to-yellow-700/80 p-8 pb-16 relative">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-black/30 backdrop-blur-md text-3xl font-unbounded font-bold text-white mb-3">
                {(profile?.nickname || username || '?').charAt(0).toUpperCase()}
              </div>
              <h2 className="text-2xl font-unbounded font-bold text-white mb-1" data-testid="profile-username">
                {profile?.nickname ? profile.nickname : username}
              </h2>
              {profile?.nickname && (
                <p className="text-white/80 text-sm">@{username}</p>
              )}
              {profile && (
                <span className="inline-block mt-2 px-3 py-1 rounded-full bg-black/30 backdrop-blur-md font-mono text-gold text-sm font-bold" data-testid="profile-xp">
                  {profile.xp} XP
                </span>
              )}
            </div>
          </div>

          <div className="-mt-10 px-6 pb-6">
            {loading ? (
              <div className="glass rounded-2xl p-5 mb-5">
                <p className="text-center text-zinc-400 py-6">Loading profile...</p>
              </div>
            ) : err ? (
              <div className="glass rounded-2xl p-5 mb-5">
                <p className="text-center text-red-400 py-6">{err}</p>
              </div>
            ) : profile && (
              <>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="glass rounded-xl p-3 text-center">
                    <Wallet size={18} className="text-gold mx-auto mb-1" />
                    <p className="text-xs text-zinc-500">Portfolio</p>
                    <p className="text-sm font-mono font-bold text-white">{formatCurrency(profile.total_value)}</p>
                  </div>
                  <div className="glass rounded-xl p-3 text-center">
                    <ChartLine size={18} className={profile.profit >= 0 ? 'text-success mx-auto mb-1' : 'text-destructive mx-auto mb-1'} />
                    <p className="text-xs text-zinc-500" title="Game-mode cash excluded">Trading Return</p>
                    <p className={`text-sm font-mono font-bold ${profile.profit >= 0 ? 'text-success' : 'text-destructive'}`}>
                      {formatPercent(profile.profit_percent)}
                    </p>
                  </div>
                  <div className="glass rounded-xl p-3 text-center">
                    <Trophy size={18} className="text-yellow-400 mx-auto mb-1" />
                    <p className="text-xs text-zinc-500">Trades</p>
                    <p className="text-sm font-mono font-bold text-white">{profile.total_trades}</p>
                  </div>
                </div>

                {/* Portfolio Holdings */}
                <div className="mb-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase size={18} className="text-gold" />
                    <h3 className="text-sm font-bold text-white uppercase tracking-wide">Portfolio Holdings</h3>
                    <span className="text-xs text-zinc-500">({holdings.length})</span>
                  </div>
                  {holdings.length === 0 ? (
                    <p className="text-sm text-zinc-500 italic">No stocks owned yet.</p>
                  ) : (
                    <div className="space-y-2" data-testid="profile-holdings">
                      {holdings.map((h, i) => {
                        const up = h.profit_loss >= 0;
                        return (
                          <div
                            key={h.symbol}
                            className="glass rounded-xl p-3 flex items-center justify-between gap-3"
                            data-testid={`profile-holding-${h.symbol}`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${up ? 'bg-emerald-500/15 text-success' : 'bg-red-500/15 text-destructive'}`}>
                                {up ? <TrendUp size={16} weight="bold" /> : <TrendDown size={16} weight="bold" />}
                              </div>
                              <div className="min-w-0">
                                <p className="font-bold text-white text-sm">{h.symbol}</p>
                                <p className="text-[11px] text-zinc-500 font-mono">{h.shares} shares</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-mono font-bold text-white">{formatCurrency(h.value)}</p>
                              <p className={`text-[11px] font-mono font-bold ${up ? 'text-success' : 'text-destructive'}`}>
                                {up ? '▲' : '▼'} {up ? '+' : ''}{formatCurrency(h.profit_loss)} ({formatPercent(h.profit_loss_percent)})
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {(profile.bonus_earnings || 0) > 0 && (
                  <div className="mb-5 px-3 py-2 rounded-lg bg-purple-500/10 border border-purple-500/20 text-center">
                    <p className="text-[11px] uppercase tracking-wider text-purple-300 mb-0.5">Game-mode bonus cash</p>
                    <p className="text-sm font-mono font-bold text-purple-200">+{formatCurrency(profile.bonus_earnings)}</p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">In portfolio, not counted as trading profit</p>
                  </div>
                )}

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Medal size={18} className="text-gold" />
                    <h3 className="text-sm font-bold text-white uppercase tracking-wide">Achievements</h3>
                    <span className="text-xs text-zinc-500">({profile.achievements.length})</span>
                  </div>
                  {profile.achievements.length === 0 ? (
                    <p className="text-sm text-zinc-500 italic">No achievements yet — complete a game mode to unlock!</p>
                  ) : (
                    <div className="flex flex-wrap gap-2" data-testid="profile-achievements">
                      {profile.achievements.map((ach, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white text-xs font-medium"
                          data-testid={`achievement-${i}`}
                        >
                          {ach}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UserProfileModal;
